import { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { getCameraConfig } from '../../../lib/config/cameras';

interface QRScannerProps {
  onScan: (qrData: string) => void;
  onError?: (error: string) => void;
}

export function QRScanner({ onScan, onError }: QRScannerProps) {
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isRequestingPermission, setIsRequestingPermission] = useState(false);

  useEffect(() => {
    const containerId = 'qr-reader';
    let cancelled = false;

    const init = () => {
      const container = document.getElementById(containerId);
      if (!container || cancelled) return;

      try {
        container.innerHTML = '';
      } catch {
        return;
      }

      let scanner: Html5Qrcode;
      try {
        scanner = new Html5Qrcode(containerId);
      } catch (err) {
        if (!cancelled) {
          const errorMsg = err instanceof Error ? err.message : 'Failed to init scanner';
          setError(errorMsg);
          onError?.(errorMsg);
        }
        return;
      }
      if (cancelled) return;
      scannerRef.current = scanner;

      const startScanner = async () => {
        try {
          const { qrCameraDeviceId } = getCameraConfig();
          const videoConfig = qrCameraDeviceId
            ? { deviceId: { exact: qrCameraDeviceId } }
            : { facingMode: 'environment' };
          await scanner.start(
            videoConfig,
            {
              fps: 10,
              qrbox: { width: 250, height: 250 },
            },
            (decodedText) => {
              onScan(decodedText);
            },
            (errorMessage) => {
              if (!errorMessage.includes('NotFoundException')) {
                console.warn('QR scan error:', errorMessage);
              }
            }
          );
          if (!cancelled) {
            setIsScanning(true);
            setError(null);
          }
        } catch (err) {
          if (!cancelled) {
            const errorMsg = err instanceof Error ? err.message : 'Failed to start camera';
            setError(errorMsg);
            onError?.(errorMsg);
          }
        }
      };

      startScanner();
    };

    const t = setTimeout(init, 0);

    return () => {
      cancelled = true;
      clearTimeout(t);
      const s = scannerRef.current;
      scannerRef.current = null;
      if (!s) return;
      const cleanUp = () => {
        const el = document.getElementById(containerId);
        if (el) el.innerHTML = '';
      };
      const isNotRunningError = (err: unknown) => {
        const msg = err instanceof Error ? err.message : String(err);
        return /not running|not paused/i.test(msg);
      };
      const handleCleanupError = (err: unknown) => {
        if (!isNotRunningError(err)) {
          console.error('QRScanner cleanup:', err);
        }
        cleanUp();
      };
      try {
        s.stop()
          .then(() => s.clear())
          .catch(handleCleanupError)
          .finally(cleanUp);
      } catch (err) {
        handleCleanupError(err);
      }
    };
  }, [onScan, onError, retryCount]);

  const handleRequestPermission = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setError('Camera access is not supported in this browser.');
      return;
    }
    setIsRequestingPermission(true);
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      stream.getTracks().forEach((t) => t.stop());
      setRetryCount((c) => c + 1);
    } catch (err) {
      const ex = err as { name?: string; message?: string };
      const msg =
        ex.name === 'NotAllowedError'
          ? 'Camera access was denied. Please allow camera in your browser.'
          : ex.message || 'Failed to get camera access';
      setError(msg);
      onError?.(msg);
    } finally {
      setIsRequestingPermission(false);
    }
  };

  return (
    <div className="relative">
      <div id="qr-reader" className="rounded-2xl overflow-hidden shadow-lg" />
      
      {error && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
          <p className="font-semibold">Camera Error</p>
          <p className="text-sm">{error}</p>
          <button
            type="button"
            onClick={handleRequestPermission}
            disabled={isRequestingPermission}
            className="mt-3 w-full py-2 px-4 rounded-lg bg-cyan-500 text-white font-medium hover:bg-cyan-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isRequestingPermission ? 'Requesting…' : 'Request camera access'}
          </button>
        </div>
      )}
      
      {isScanning && !error && (
        <div className="mt-4 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-50 rounded-full">
            <div className="w-2 h-2 bg-cyan-500 rounded-full animate-pulse" />
            <span className="text-sm text-cyan-700 font-medium">Scanning for QR code...</span>
          </div>
        </div>
      )}
    </div>
  );
}
